import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TabsContent } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle, Plus } from 'lucide-react';
import { useContext, useState } from 'react';
import { ApuracaoDetalhadaContext } from '@/contexts/ApuracaoDetalhadaContext';

export function CreditosLc214Tab() {
  const ctx = useContext(ApuracaoDetalhadaContext);
  if (!ctx) {
    throw new Error('CreditosLc214Tab deve ser usado dentro de <ApuracaoDetalhadaProvider>');
  }

  const [showIncluirCreditoModal, setShowIncluirCreditoModal] = useState(false);
  const [novoCreditoForm, setNovoCreditoForm] = useState({
    tipo: '',
    descricao: '',
    valorIBS: '',
    valorCBS: '',
    periodo: '',
    fundamentacao: '',
    observacoes: '',
  });

  return (
    <>
      <TabsContent value="creditos-lc214" className="space-y-6">
        <Card className="border-2 border-blue-200 bg-blue-50/30">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-6 w-6 text-blue-600" />
                <div>
                  <CardTitle className="text-xl text-blue-900">
                    Créditos Adicionais Previstos na LC 214/2025
                  </CardTitle>
                  <p className="text-sm text-blue-700 mt-1">
                    Créditos presumidos, ressarcimentos e outros benefícios fiscais da Reforma
                    Tributária
                  </p>
                </div>
              </div>
              <Button
                className="bg-primary hover:bg-primary/90"
                onClick={() => setShowIncluirCreditoModal(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Incluir Crédito
              </Button>
            </div>
          </CardHeader>
        </Card>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Tipos de Créditos Disponíveis</h3>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Créditos Financeiros</CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-start justify-between pb-4 bg-muted/30 p-4 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">01 - Crédito Financeiro</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Crédito vinculado à aquisição de bens, serviços ou direitos utilizados na
                    atividade econômica. É apropriado com base nos documentos fiscais de entrada
                    (Art. 25 a 28 da LC 214/2025).
                  </p>
                  <a href="#" className="text-sm text-blue-600 hover:underline">
                    Art. 25 a 28 da LC 214/2025
                  </a>
                </div>
                <div className="flex gap-2 ml-4">
                  <Badge variant="outline">IBS</Badge>
                  <Badge variant="outline">CBS</Badge>
                </div>
              </div>

              <div className="flex items-start justify-between pb-4 bg-muted/30 p-4 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">
                    02 - Crédito sobre Bens do Ativo Imobilizado
                  </h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Crédito apropriado sobre a aquisição de bens do ativo imobilizado utilizados na
                    atividade econômica. Pode ser apropriado de forma imediata ou parcelada conforme
                    regulamentação.
                  </p>
                  <a href="#" className="text-sm text-blue-600 hover:underline">
                    Art. 27 da LC 214/2025
                  </a>
                </div>
                <div className="flex gap-2 ml-4">
                  <Badge variant="outline">IBS</Badge>
                  <Badge variant="outline">CBS</Badge>
                </div>
              </div>

              <div className="flex items-start justify-between pb-4 bg-muted/30 p-4 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">03 - Crédito sobre Serviços de Transporte</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Crédito sobre serviços de transporte de cargas e passageiros, tanto de entrada
                    quanto de saída, quando vinculado a atividades econômicas de contribuintes.
                  </p>
                  <a href="#" className="text-sm text-blue-600 hover:underline">
                    Art. 28, §1º da LC 214/2025
                  </a>
                </div>
                <div className="flex gap-2 ml-4">
                  <Badge variant="outline">IBS</Badge>
                  <Badge variant="outline">CBS</Badge>
                </div>
              </div>

              <div className="flex items-start justify-between pb-4 bg-muted/30 p-4 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">
                    04 - Crédito sobre Energia Elétrica e Telecomunicações
                  </h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Crédito sobre consumo de energia elétrica e serviços de telecomunicações
                    utilizados no processo produtivo ou na prestação de serviços.
                  </p>
                  <a href="#" className="text-sm text-blue-600 hover:underline">
                    Art. 26, §4º da LC 214/2025
                  </a>
                </div>
                <div className="flex gap-2 ml-4">
                  <Badge variant="outline">IBS</Badge>
                  <Badge variant="outline">CBS</Badge>
                </div>
              </div>

              <div className="flex items-start justify-between pb-4 bg-muted/30 p-4 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">
                    11 - Crédito sobre Devoluções e Cancelamentos
                  </h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Crédito decorrente de devoluções de mercadorias, cancelamentos de vendas ou
                    ajustes de operações anteriores tributadas.
                  </p>
                  <a href="#" className="text-sm text-blue-600 hover:underline">
                    Art. 32 da LC 214/2025
                  </a>
                </div>
                <div className="flex gap-2 ml-4">
                  <Badge variant="outline">IBS</Badge>
                  <Badge variant="outline">CBS</Badge>
                </div>
              </div>

              <div className="flex items-start justify-between bg-muted/30 p-4 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">12 - Crédito sobre Insumos Importados</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Crédito sobre IBS e CBS pagos na importação de bens e serviços utilizados como
                    insumo na atividade econômica do contribuinte.
                  </p>
                  <a href="#" className="text-sm text-blue-600 hover:underline">
                    Art. 29 da LC 214/2025
                  </a>
                </div>
                <div className="flex gap-2 ml-4">
                  <Badge variant="outline">IBS</Badge>
                  <Badge variant="outline">CBS</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Créditos Presumidos</CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-start justify-between pb-4 bg-muted/30 p-4 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">
                    13 - Crédito sobre Operações com Split Payment
                  </h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Crédito apropriado em operações onde houve recolhimento via split payment
                    (recolhimento automático na liquidação financeira).
                  </p>
                  <a href="#" className="text-sm text-blue-600 hover:underline">
                    Art. 27, §3º da LC 214/2025
                  </a>
                </div>
                <div className="flex gap-2 ml-4">
                  <Badge variant="outline">IBS</Badge>
                  <Badge variant="outline">CBS</Badge>
                </div>
              </div>

              <div className="flex items-start justify-between pb-4 bg-muted/30 p-4 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">14 - Crédito sobre Operações Diferidas</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Apropriação de crédito referente a operações com diferimento de imposto, quando
                    ocorrer o encerramento do diferimento.
                  </p>
                  <a href="#" className="text-sm text-blue-600 hover:underline">
                    Art. 33 da LC 214/2025
                  </a>
                </div>
                <div className="flex gap-2 ml-4">
                  <Badge variant="outline">IBS</Badge>
                  <Badge variant="outline">CBS</Badge>
                </div>
              </div>

              <div className="flex items-start justify-between pb-4 bg-muted/30 p-4 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">
                    05 - Crédito Presumido - Setor Agropecuário
                  </h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Crédito presumido concedido ao setor agropecuário como forma de redução da carga
                    tributária, compensando custos específicos do setor. Percentual definido por
                    regulamentação específica.
                  </p>
                  <a href="#" className="text-sm text-blue-600 hover:underline">
                    Art. 57 da LC 214/2025
                  </a>
                </div>
                <div className="flex gap-2 ml-4">
                  <Badge variant="outline">IBS</Badge>
                  <Badge variant="outline">CBS</Badge>
                </div>
              </div>

              <div className="flex items-start justify-between pb-4 bg-muted/30 p-4 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">06 - Crédito Presumido - Regime Específico</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Crédito presumido para contribuintes enquadrados em regime específico de
                    tributação, conforme definido pelo Comitê Gestor do IBS ou Receita Federal para
                    CBS.
                  </p>
                  <a href="#" className="text-sm text-blue-600 hover:underline">
                    Art. 59 da LC 214/2025
                  </a>
                </div>
                <div className="flex gap-2 ml-4">
                  <Badge variant="outline">IBS</Badge>
                  <Badge variant="outline">CBS</Badge>
                </div>
              </div>

              <div className="flex items-start justify-between pb-4 bg-muted/30 p-4 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">
                    07 - Crédito Presumido - Compras de Não Contribuintes
                  </h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Crédito presumido para operações com não contribuintes (pessoas físicas ou
                    jurídicas não sujeitas ao IBS/CBS), calculado sobre o valor da operação.
                  </p>
                  <a href="#" className="text-sm text-blue-600 hover:underline">
                    Art. 59 da LC 214/2025
                  </a>
                </div>
                <div className="flex gap-2 ml-4">
                  <Badge variant="outline">IBS</Badge>
                  <Badge variant="outline">CBS</Badge>
                </div>
              </div>

              <div className="flex items-start justify-between bg-muted/30 p-4 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">
                    08 - Crédito Presumido - Zona Franca de Manaus
                  </h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Crédito presumido para operações destinadas à Zona Franca de Manaus e Áreas de
                    Livre Comércio, conforme benefícios mantidos pela Constituição Federal.
                  </p>
                  <a href="#" className="text-sm text-blue-600 hover:underline">
                    Art. 60 da LC 214/2025
                  </a>
                </div>
                <div className="flex gap-2 ml-4">
                  <Badge variant="outline">IBS</Badge>
                  <Badge variant="outline">CBS</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Créditos Extemporâneos</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between bg-muted/30 p-4 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">09 - Crédito Extemporâneo</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Crédito apropriado fora do prazo regulamentar, sujeito a limitações e condições
                    específicas. Pode sofrer redução do valor conforme tempo de atraso.
                  </p>
                  <a href="#" className="text-sm text-blue-600 hover:underline">
                    Art. 35 da LC 214/2025
                  </a>
                </div>
                <div className="flex gap-2 ml-4">
                  <Badge variant="outline">IBS</Badge>
                  <Badge variant="outline">CBS</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Ressarcimento</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between bg-muted/30 p-4 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">10 - Ressarcimento de Saldo Credor</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Solicitação de ressarcimento do saldo credor acumulado quando não há débitos
                    suficientes para compensação. Pode ser em dinheiro ou mediante transferência.
                  </p>
                  <a href="#" className="text-sm text-blue-600 hover:underline">
                    Art. 39 da LC 214/2025
                  </a>
                </div>
                <div className="flex gap-2 ml-4">
                  <Badge variant="outline">IBS</Badge>
                  <Badge variant="outline">CBS</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Outros Créditos</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between bg-muted/30 p-4 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">
                    15 - Transferência de Crédito entre Estabelecimentos
                  </h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Crédito recebido por transferência de outro estabelecimento da mesma empresa,
                    permitindo a centralização ou distribuição de créditos.
                  </p>
                  <a href="#" className="text-sm text-blue-600 hover:underline">
                    Art. 40 da LC 214/2025
                  </a>
                </div>
                <div className="flex gap-2 ml-4">
                  <Badge variant="outline">IBS</Badge>
                  <Badge variant="outline">CBS</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </TabsContent>

      <Dialog open={showIncluirCreditoModal} onOpenChange={setShowIncluirCreditoModal}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Incluir Novo Crédito</DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label htmlFor="tipo-credito">Tipo de Crédito</Label>
              <Select
                value={novoCreditoForm.tipo}
                onValueChange={value => setNovoCreditoForm(prev => ({ ...prev, tipo: value }))}
              >
                <SelectTrigger id="tipo-credito">
                  <SelectValue placeholder="Selecione o tipo de crédito" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="01">01 - Crédito Financeiro</SelectItem>
                  <SelectItem value="02">02 - Crédito sobre Bens do Ativo Imobilizado</SelectItem>
                  <SelectItem value="03">03 - Crédito sobre Serviços de Transporte</SelectItem>
                  <SelectItem value="04">
                    04 - Crédito sobre Energia Elétrica e Telecomunicações
                  </SelectItem>
                  <SelectItem value="05">05 - Crédito Presumido - Setor Agropecuário</SelectItem>
                  <SelectItem value="06">06 - Crédito Presumido - Regime Específico</SelectItem>
                  <SelectItem value="07">
                    07 - Crédito Presumido - Compras de Não Contribuintes
                  </SelectItem>
                  <SelectItem value="08">08 - Crédito Presumido - Zona Franca de Manaus</SelectItem>
                  <SelectItem value="09">09 - Crédito Extemporâneo</SelectItem>
                  <SelectItem value="10">10 - Ressarcimento de Saldo Credor</SelectItem>
                  <SelectItem value="11">11 - Crédito sobre Devoluções e Cancelamentos</SelectItem>
                  <SelectItem value="12">12 - Crédito sobre Insumos Importados</SelectItem>
                  <SelectItem value="13">13 - Crédito sobre Operações com Split Payment</SelectItem>
                  <SelectItem value="14">14 - Crédito sobre Operações Diferidas</SelectItem>
                  <SelectItem value="15">
                    15 - Transferência de Crédito entre Estabelecimentos
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição</Label>
              <Textarea
                id="descricao"
                placeholder="Descreva o crédito"
                value={novoCreditoForm.descricao}
                onChange={e => setNovoCreditoForm(prev => ({ ...prev, descricao: e.target.value }))}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="valor-ibs">Valor IBS (R$)</Label>
                <Input
                  id="valor-ibs"
                  type="number"
                  step="0.01"
                  placeholder="0,00"
                  value={novoCreditoForm.valorIBS}
                  onChange={e =>
                    setNovoCreditoForm(prev => ({ ...prev, valorIBS: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="valor-cbs">Valor CBS (R$)</Label>
                <Input
                  id="valor-cbs"
                  type="number"
                  step="0.01"
                  placeholder="0,00"
                  value={novoCreditoForm.valorCBS}
                  onChange={e =>
                    setNovoCreditoForm(prev => ({ ...prev, valorCBS: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="periodo">Período de Apuração</Label>
              <Input
                id="periodo"
                type="month"
                value={novoCreditoForm.periodo}
                onChange={e => setNovoCreditoForm(prev => ({ ...prev, periodo: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fundamentacao">Fundamentação Legal</Label>
              <div className="relative">
                <Textarea
                  id="fundamentacao"
                  placeholder="Informe a base legal do crédito"
                  value={novoCreditoForm.fundamentacao}
                  onChange={e =>
                    setNovoCreditoForm(prev => ({ ...prev, fundamentacao: e.target.value }))
                  }
                  rows={3}
                />
                <button
                  type="button"
                  className="absolute bottom-2 right-2 p-1.5 bg-green-500 hover:bg-green-600 text-white rounded-full transition-colors"
                  title="Adicionar fundamentação"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="observacoes">Observações (Opcional)</Label>
              <Textarea
                id="observacoes"
                placeholder="Informações adicionais"
                value={novoCreditoForm.observacoes}
                onChange={e =>
                  setNovoCreditoForm(prev => ({ ...prev, observacoes: e.target.value }))
                }
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowIncluirCreditoModal(false);
                setNovoCreditoForm({
                  tipo: '',
                  descricao: '',
                  valorIBS: '',
                  valorCBS: '',
                  periodo: '',
                  fundamentacao: '',
                  observacoes: '',
                });
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                setShowIncluirCreditoModal(false);
                setNovoCreditoForm({
                  tipo: '',
                  descricao: '',
                  valorIBS: '',
                  valorCBS: '',
                  periodo: '',
                  fundamentacao: '',
                  observacoes: '',
                });
              }}
            >
              Adicionar Crédito
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
